import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import homeSrc from "@/assets/images/menu_assets/menu-sidebar-icons/mobile/sidebar_icon-home-default_mobile.svg";
import screeningSrc from "@/assets/images/menu_assets/menu-sidebar-icons/mobile/sidebar_icon-screening-default_mobile.svg";
import calanderSrc from "@/assets/images/menu_assets/menu-sidebar-icons/mobile/sidebar_icon-calendar-default_mobile.svg";
import playerSrc from "@/assets/images/menu_assets/menu-sidebar-icons/mobile/sidebar_icon-player-default_mobile.svg";
import { Routes } from "@/routes/router";
import useAuthorization from "@/hooks/useAuthorization";

interface NavigationBottombarProps {
  scrollContainerRef: RefObject<HTMLElement | null>;
}

const SCROLL_THRESHOLD = 10;
const FORM_ELEMENTS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

export const NavigationBottombar = ({ scrollContainerRef }: NavigationBottombarProps): JSX.Element | null => {
  const { t } = useTranslation();
  const location = useLocation();
  const { hasLevel } = useAuthorization();
  // Same rule as the side menu: Motion DNA replaces the classic screening, so
  // the coach who is on it should not be handed a shortcut back to the old one.
  const showScreening = hasLevel("screening") && !hasLevel("motiondna");
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollTop = useRef(0);
  const isFocused = useRef(false);

  const isFormElement = useCallback((el: EventTarget | null) => {
    return el instanceof HTMLElement && FORM_ELEMENTS.has(el.tagName);
  }, []);

  const toggleVisibility = useCallback(() => {
    setIsVisible(prev => !prev);
  }, []);

  // Hide on scroll down, show on scroll up
  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const handleScroll = () => {
      if (isFocused.current) return;
      const currentScrollTop = container.scrollTop;
      const delta = currentScrollTop - lastScrollTop.current;

      if (Math.abs(delta) < SCROLL_THRESHOLD) return;

      setIsVisible(delta < 0);
      lastScrollTop.current = currentScrollTop;
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [scrollContainerRef]);

  // Hide when a form element is focused (covers non-scrollable pages)
  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const handleFocusIn = (e: FocusEvent) => {
      if (isFormElement(e.target)) {
        isFocused.current = true;
        setIsVisible(false);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (isFormElement(e.target)) {
        isFocused.current = false;
        setIsVisible(true);
      }
    };

    container.addEventListener("focusin", handleFocusIn);
    container.addEventListener("focusout", handleFocusOut);
    return () => {
      container.removeEventListener("focusin", handleFocusIn);
      container.removeEventListener("focusout", handleFocusOut);
    };
  }, [scrollContainerRef, isFormElement]);

  if (location.pathname.includes('/screening-ai') || location.pathname === '/chat') {
    return null;
  }

  return (
    <>
      {/* Toggle handle - always anchored at screen bottom */}
      <button
        onClick={toggleVisibility}
        className={`fixed left-1/2 -translate-x-1/2 z-50 w-10 h-6 bg-bg-sand rounded-t-lg flex items-center justify-center shadow-md transition-all duration-300 ease-in-out ${isVisible ? "bottom-[56px]" : "bottom-0"}`}
        aria-label={isVisible ? "Hide navigation" : "Show navigation"}
      >
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isVisible ? "rotate-0" : "rotate-180"}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Navigation bar */}
      <nav className={`fixed bottom-0 left-0 w-full z-50 bg-bg-sand rounded-full flex justify-around items-center p-2 shadow-lg transition-transform duration-300 ease-in-out ${isVisible ? "translate-y-0" : "translate-y-full"}`}>
        <Link to={Routes.PORTAL} className="flex flex-col items-center">
          <img className="w-[25px] h-[25px]" alt="Home Icon" src={homeSrc} />
          <span className="font-desktop-text-xs text-black text-center">{t("sidebar.menu.home")}</span>
        </Link>

        {showScreening && (
          <Link to={Routes.PORTAL_SCREENING} className="flex flex-col items-center">
            <img className="w-[25px] h-[25px]" alt="Screening Icon" src={screeningSrc} />
            <span className="font-desktop-text-xs text-black text-center">{t("sidebar.menu.screening")}</span>
          </Link>
        )}

        <Link to={Routes.PORTAL_CALENDAR} className="flex flex-col items-center">
          <img className="w-[25px] h-[25px]" alt="Calendar Icon" src={calanderSrc} />
          <span className="font-desktop-text-xs text-black text-center">{t("sidebar.menu.calendar")}</span>
        </Link>

        <Link to={Routes.PORTAL_PLAYER} className="flex flex-col items-center">
          <img className="w-[25px] h-[25px]" alt="Players Icon" src={playerSrc} />
          <span className="font-desktop-text-xs text-black text-center">{t("sidebar.menu.players")}</span>
        </Link>
      </nav>
    </>
  );
};
